/* state/commute-data.js — laddar commute-matriserna + origin-listan, helpers.
 *
 * Tre filer:
 *   data/commute/origins.json            — 2017 tätort-centroider med kod/namn/kom_kod
 *   data/commute/transit_matrix.bin      — kollektivtrafik-matris (RAPTOR/GTFS)
 *   data/commute/car_matrix.bin          — bil-matris (OSRM, valfri)
 *
 * Format på bin-filerna ('MMTM' magic, version 1):
 *   bytes  0..3   = b"MMTM"
 *   bytes  4..7   = uint32 LE version (=1)
 *   bytes  8..11  = uint32 LE N (antal origins)
 *   bytes 12..15  = uint32 LE reserverat (0)
 *   bytes 16..   = N*N * 2 bytes uint16 LE — minuter eller 0xFFFF=unreachable
 *
 * car_matrix.bin är OPTIONAL — om filen inte finns på servern (t.ex. innan
 * Phase 3 är deployad) loggar vi en warning och carMin/kommunCarMinTime
 * returnerar null. Bil-mode i pendlarläget visar då tomma kommuner istället
 * för att krascha eller visa fel data.
 *
 * Helpers:
 *   loadCommuteData()           async — fetchar alla tre filer
 *   getOrigins()                Array<{kod,namn,kom_kod,bef,lat,lon,i}>
 *   getMatrix()                 Uint16Array (N*N) transit, eller null
 *   getCarMatrix()              Uint16Array (N*N) car, eller null
 *   getN()                      number eller 0
 *   nearestTatort(lat, lon)     {origin, distance_m} | null
 *   commuteMin(fromI, toI)      number eller null (unreachable)
 *   carMin(fromI, toI)          number eller null
 *   kommunMinTime(kommunKod, toI) number eller null — bästa tätort i kommunen
 *   kommunCarMinTime(kommunKod, toI) number eller null — bästa via bil
 *
 * Snapping: bygger en lat-grouped bucket-grid för O(1) nearest-tätort-lookup.
 */

/* ⚠ MATRISERNA BOR I SHARED-MAPPEN, `origins.json` gör det INTE.
 * `origins.json` är git-spårad (216 kB) och följer med releasen som vanligt.
 * De två matriserna är gitignorerade och 8,1 MB styck: de låg tidigare som
 * OSPÅRADE filer i VPS:ens git-checkout och överlevde bara för att deployens
 * rsync kopierade med dem — ett `git clean` hade raderat dem tyst, och
 * `car_matrix` är OPTIONAL nedan, så bilmatrisen hade försvunnit utan ett ljud
 * (§9 #25). Sedan 2026-09-10 ligger de i `<shared>/_generated/commute/` med
 * `.br`-syskon: 16,3 MB → 2,2 MB över tråden (transit-matrisen komprimerar
 * 98 %). ⚠ Shared-mappen står UTANFÖR deployens precompress-steg — `.br`
 * skrivs av byggskripten själva. Se docs/SHARED_DATA.md. */
const ORIGINS_URL    = '/data/commute/origins.json?v=1';
const MATRIX_URL     = '/data/_generated/commute/transit_matrix.bin?v=2';
const CAR_MATRIX_URL = '/data/_generated/commute/car_matrix.bin?v=2';

const UNREACHABLE = 0xFFFF;
const EARTH_R_M = 6371008.8;

// Snap-grid step (samma som backend's POLY_GRID_STEP, 0.05° ≈ 5.5 km lat)
const SNAP_STEP = 0.05;

let origins = null;        // sorterat på kod, har stable .i
let matrix = null;         // Uint16Array (transit)
let carMatrix = null;      // Uint16Array (car) eller null om filen saknas
let N = 0;
let snapGrid = null;       // Map<key, list<origin-idx>>
let kodToIdx = null;       // Map<kod, idx>
let kommunToOriginIdx = null;  // Map<kom_kod, list<origin-idx>>
let promise = null;

function _haversine_m(lat1, lon1, lat2, lon2) {
    const p1 = lat1 * Math.PI / 180;
    const p2 = lat2 * Math.PI / 180;
    const dp = (lat2 - lat1) * Math.PI / 180;
    const dl = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
    return 2 * EARTH_R_M * Math.asin(Math.sqrt(a));
}

function _buildIndices() {
    snapGrid = new Map();
    kodToIdx = new Map();
    kommunToOriginIdx = new Map();
    for (let i = 0; i < origins.length; i++) {
        const o = origins[i];
        kodToIdx.set(o.kod, i);
        const key = `${Math.floor(o.lat / SNAP_STEP)},${Math.floor(o.lon / SNAP_STEP)}`;
        if (!snapGrid.has(key)) snapGrid.set(key, []);
        snapGrid.get(key).push(i);
        if (!kommunToOriginIdx.has(o.kom_kod)) kommunToOriginIdx.set(o.kom_kod, []);
        kommunToOriginIdx.get(o.kom_kod).push(i);
    }
}

async function _fetchMatrix(url) {
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`commute matrix fetch ${resp.status}`);
    const buf = await resp.arrayBuffer();
    const view = new DataView(buf);
    // Magic check
    if (view.getUint8(0) !== 0x4D || view.getUint8(1) !== 0x4D ||
        view.getUint8(2) !== 0x54 || view.getUint8(3) !== 0x4D) {
        throw new Error('matrix: bad magic');
    }
    const version = view.getUint32(4, true);
    if (version !== 1) throw new Error(`matrix: bad version ${version}`);
    const n = view.getUint32(8, true);
    if (n * n * 2 + 16 !== buf.byteLength) {
        throw new Error(`matrix: size mismatch (n=${n}, bytes=${buf.byteLength})`);
    }
    return { n, data: new Uint16Array(buf, 16, n * n) };
}

/* ⚠ BILMATRISEN LADDAS SEPARAT sedan 2026-09-15. Den är 2 MB brotli mot
 * transitmatrisens 165 KB, och filtervyns "storstad inom N minuter" behöver
 * bara transit. `loadCommuteData()` utan argument laddar som förut båda (för
 * Avstånd-villkorets bil-läge); `{ bil: false }` hoppar över bilen, och ett
 * senare anrop utan argument hämtar den då. */
let carPromise = null;
function loadCarMatrix() {
    if (carMatrix) return Promise.resolve(carMatrix);
    if (!carPromise) {
        carPromise = _fetchMatrix(CAR_MATRIX_URL)
            .then((carRes) => {
                if (carRes.n === N) carMatrix = carRes.data;
                else console.warn(`[commute-data] car-matrix N=${carRes.n} != transit N=${N} — ignorerar`);
                return carMatrix;
            })
            .catch((err) => {
                // car-matrix är optional: saknas filen faller bil-mode tillbaka till null.
                console.warn('[commute-data] car-matrix saknas — bil-mode utan data:', err.message);
                carPromise = null;
                return null;
            });
    }
    return carPromise;
}

export async function loadCommuteData({ bil = true } = {}) {
    if (!promise) {
        // a11y: annonsera laddstart för skärmläsare. window.mmAnnounce
        // sätts av js/shared.js — fail tyst om den inte är där.
        try { window.mmAnnounce && window.mmAnnounce('Hämtar pendlardata, det kan ta en stund'); } catch (_) {}
        promise = (async () => {
            try {
                const [originsResp, matRes] = await Promise.all([
                    fetch(ORIGINS_URL).then((r) => {
                        if (!r.ok) throw new Error(`origins fetch ${r.status}`);
                        return r.json();
                    }),
                    _fetchMatrix(MATRIX_URL),
                ]);
                origins = originsResp;
                matrix = matRes.data;
                N = matRes.n;
                _buildIndices();
                try { window.mmAnnounce && window.mmAnnounce('Pendlardata redo'); } catch (_) {}
            } catch (err) {
                console.error('[commute-data] load failed:', err);
                origins = []; matrix = null; carMatrix = null; N = 0;
                _buildIndices();
                try { window.mmAnnounce && window.mmAnnounce('Kunde inte hämta pendlardata'); } catch (_) {}
                throw err;
            }
        })();
    }
    await promise;
    if (bil) await loadCarMatrix();
    return { origins, matrix, carMatrix, N };
}

export function getOrigins()    { return origins || []; }
export function getMatrix()     { return matrix; }
export function getCarMatrix()  { return carMatrix; }
export function getN()          { return N; }
export function isLoaded()      { return !!matrix; }
export function isCarLoaded()   { return !!carMatrix; }

/**
 * Hitta närmsta tätort till (lat, lon). Söker i 3×3 grid-buckets runt
 * punkten, faller tillbaka till full scan om grid är glest. Returnerar
 * { origin, distance_m } eller null om origins ej laddat.
 */
export function nearestTatort(lat, lon) {
    if (!origins || origins.length === 0) return null;
    const baseLa = Math.floor(lat / SNAP_STEP);
    const baseLo = Math.floor(lon / SNAP_STEP);
    let best = null; let bestDist = Infinity;

    function consider(i) {
        const o = origins[i];
        const d = _haversine_m(lat, lon, o.lat, o.lon);
        if (d < bestDist) { bestDist = d; best = o; }
    }

    // Sök i utvidgade ringar tills vi hittar något
    for (let radius = 1; radius <= 6; radius++) {
        for (let dla = -radius; dla <= radius; dla++) {
            for (let dlo = -radius; dlo <= radius; dlo++) {
                if (Math.max(Math.abs(dla), Math.abs(dlo)) !== radius && radius > 1) continue;
                const ids = snapGrid.get(`${baseLa + dla},${baseLo + dlo}`);
                if (ids) for (const i of ids) consider(i);
            }
        }
        if (best) break;
    }
    // Fallback: full scan om grid var tom (mycket avlägsen punkt)
    if (!best) {
        for (let i = 0; i < origins.length; i++) consider(i);
    }
    return best ? { origin: best, distance_m: bestDist } : null;
}

/** Restid (minuter) mellan två origin-indexes, eller null vid unreachable. */
export function commuteMin(fromI, toI) {
    if (!matrix || N === 0) return null;
    if (fromI < 0 || fromI >= N || toI < 0 || toI >= N) return null;
    const v = matrix[fromI * N + toI];
    return v === UNREACHABLE ? null : v;
}

/**
 * Bästa restid från ALLA tätorter i en kommun till `toI`.
 * Representerar "om jag bor optimalt i kommunen, hur snabbt kan jag pendla".
 * Returnerar null om kommunen saknar reachable tätorter.
 */
export function kommunMinTime(kommunKod, toI) {
    if (!matrix || N === 0) return null;
    const indices = kommunToOriginIdx.get(kommunKod);
    if (!indices || indices.length === 0) return null;
    let best = null;
    for (const i of indices) {
        const v = matrix[i * N + toI];
        if (v === UNREACHABLE) continue;
        if (best === null || v < best) best = v;
    }
    return best;
}

/* Restid (min, kollektivt) från kommunens bäst placerade tätort till närmaste av
 * Stockholm, Göteborg och Malmö — filtervyns "storstad inom N minuter" och
 * KPI:n `pendling:storstad:transit`. Tätortskoderna är SCB:s (origins.json);
 * null när ingen av dem nås inom matrisens tak (81 kommuner, mätt 2026-09-15).
 * ⚠ Bara transit: bilmatrisen är 2 MB brotli och laddas inte för ett filter. */
const STORSTAD_KODER = ['0180TC101', '1480TC108', '1280TC106'];
export function kommunMinTillStorstad(kommunKod) {
    if (!matrix || !kodToIdx) return null;
    let best = null;
    for (const k of STORSTAD_KODER) {
        const toI = kodToIdx.get(k);
        if (toI == null) continue;
        const v = kommunMinTime(kommunKod, toI);
        if (v != null && (best === null || v < best)) best = v;
    }
    return best;
}

export function getKodIdx(kod) {
    return kodToIdx ? (kodToIdx.get(kod) ?? null) : null;
}

/**
 * Bil-restid (minuter) mellan två tätort-indexes. Slår upp i car_matrix.bin
 * (OSRM driving-profile, byggd månadsvis från Geofabrik OSM-data).
 * Returnerar null om car-matrix saknas eller cellen är unreachable
 * (>= cap, default 8h, eller på en ö utan vägförbindelse).
 */
export function carMin(fromI, toI) {
    if (!carMatrix || N === 0) return null;
    if (fromI < 0 || fromI >= N || toI < 0 || toI >= N) return null;
    const v = carMatrix[fromI * N + toI];
    return v === UNREACHABLE ? null : v;
}

export function kommunCarMinTime(kommunKod, toI) {
    if (!carMatrix || N === 0) return null;
    const indices = kommunToOriginIdx.get(kommunKod);
    if (!indices || indices.length === 0) return null;
    let best = null;
    for (const i of indices) {
        const v = carMatrix[i * N + toI];
        if (v === UNREACHABLE) continue;
        if (best === null || v < best) best = v;
    }
    return best;
}

/**
 * Närmsta fågelväg (haversine, meter) från (lat, lon) till någon tätort
 * i kommunen. Används av __radius__-rule i mode='euclid' så user kan
 * filtrera på rent geografiskt avstånd utan att behöva commute-matrix-data
 * för exakt restid. Förlitar sig på origins-listan (samma indexering som
 * kommun→tätorter), så den fungerar oberoende av om transit/car-matriser
 * är laddade.
 */
export function kommunMinEuclidM(kommunKod, lat, lon) {
    if (!origins || origins.length === 0) return null;
    if (!kommunToOriginIdx) return null;
    const indices = kommunToOriginIdx.get(kommunKod);
    if (!indices || indices.length === 0) return null;
    let best = null;
    for (const i of indices) {
        const o = origins[i];
        const d = _haversine_m(lat, lon, o.lat, o.lon);
        if (best === null || d < best) best = d;
    }
    return best;
}
